import { default as Dropdown } from 'antd/es/dropdown';
import type { MenuProps } from 'antd/es/menu';
import { Ellipsis } from 'lucide-react';
import { useState } from 'react';

import { hasValue } from 'utils/hasValue';

import type { AuthScheme } from 'api';
import { Restricted } from 'containers/Restricted';

import {
  CARD_ACTIONS_EXPORT_OPTION_TEST_ID,
  CARD_ACTIONS_TOGGLE_OPTION_TEST_ID,
  CARD_ACTIONS_TRIGGER_TEST_ID,
  EXPORT_DISABLED_REASON,
  EXPORT_LABEL,
  TOGGLE_DISABLE_LABEL,
  TOGGLE_ENABLE_LABEL
} from './constants';
import { ExportKeyModal } from './ExportKeyModal';
import { ToggleSchemeStatusModal } from './ToggleSchemeStatusModal';

type AuthenticationCardActionsProps = {
  authenticationKey: AuthScheme;
};

type OpenModal = 'toggle' | 'export' | null;

export const AuthenticationCardActions = ({
  authenticationKey
}: AuthenticationCardActionsProps) => {
  const [modal, setModal] = useState<OpenModal>(null);
  const canExport =
    hasValue(authenticationKey.certificate) &&
    authenticationKey.certificate.length > 0;
  const closeModal = () => setModal(null);

  const items: MenuProps['items'] = [
    {
      key: 'export',
      disabled: !canExport,
      onClick: () => setModal('export'),
      label: (
        <span
          data-testid={CARD_ACTIONS_EXPORT_OPTION_TEST_ID(
            authenticationKey.uuid
          )}
        >
          {EXPORT_LABEL}
          {!canExport && (
            <span className="block text-xs text-content-secondary">
              {EXPORT_DISABLED_REASON}
            </span>
          )}
        </span>
      )
    },
    {
      key: 'toggle',
      danger: authenticationKey.enabled,
      onClick: () => setModal('toggle'),
      label: (
        <span
          data-testid={CARD_ACTIONS_TOGGLE_OPTION_TEST_ID(
            authenticationKey.uuid
          )}
        >
          {authenticationKey.enabled
            ? TOGGLE_DISABLE_LABEL
            : TOGGLE_ENABLE_LABEL}
        </span>
      )
    }
  ];

  return (
    <Restricted requiredPermission="developers:manage">
      <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
        <button
          type="button"
          aria-label="Key actions"
          data-testid={CARD_ACTIONS_TRIGGER_TEST_ID(authenticationKey.uuid)}
          className="cursor-pointer appearance-none border-0 bg-transparent p-0 text-content-primary hover:text-content-secondary"
        >
          <Ellipsis className="size-5" />
        </button>
      </Dropdown>
      <ToggleSchemeStatusModal
        authenticationKey={authenticationKey}
        open={modal === 'toggle'}
        onCancel={closeModal}
        onConfirmed={closeModal}
      />
      {canExport && (
        <ExportKeyModal
          authenticationKey={authenticationKey}
          open={modal === 'export'}
          onClose={closeModal}
        />
      )}
    </Restricted>
  );
};
